import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { iif, insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PluginWithSettings } from 'src/app/services/dtos/pluginWithSettings';
import { PluginState } from 'src/app/services/enums/pluginState.enum';
import { Plugin } from '../../services/dtos/plugin';
import { PluginsService } from './../../services/plugins.service';

import { PluginForm } from 'src/app/services/dtos/pluginForm';
import {
  AddPluginLaboratorySpecificSetting,
  ClearCurrentPluginId, DeletePlugin, DeletePluginLaboratorySpecificSetting, EnablePluginTestMode, FetchAllPluginsBasicInfo,
  FetchPlugin, FetchPluginsForCustomer, SetCurrentPluginId, StartPlugin,
  StopPlugin, UpdatePlugin, UpdatePluginConfigurationFile, UpdatePluginLaboratorySpecificSetting
} from './plugins.actions';

export class PluginsStateModel {
  public selectedPluginId!: string;
  public plugins!: PluginWithSettings[];
  public arePluginsLoaded!: boolean;
  public arePluginsLoading!: boolean;
  public customerPlugins!: PluginWithSettings[];
}

const defaults = {
  selectedPluginId: '',
  plugins: [],
  arePluginsLoaded: false,
  arePluginsLoading: false,
  customerPlugins: [],
};

@State<PluginsStateModel>({
  name: 'pluginsState',
  defaults
})

@Injectable()
export class PluginsState {
  public constructor(private pluginsService: PluginsService) { }

  @Selector()
  public static arePluginsLoadedOrLoading(state: PluginsStateModel): boolean {
    return state.arePluginsLoaded || state.arePluginsLoading;
  }

  @Selector()
  public static getSelectedPluginId(state: PluginsStateModel): string {
    return state.selectedPluginId;
  }

  @Selector()
  public static getAllPlugins(state: PluginsStateModel): Plugin[] {
    return state.plugins as Plugin[];
  }

  @Selector()
  public static getAllPluginsForCustomer(state: PluginsStateModel): Plugin[] {
    return state.customerPlugins as Plugin[];
  }

  @Selector()
  public static getSelectedPlugin(state: PluginsStateModel): PluginWithSettings | undefined {
    return state.plugins.find(({ id }) => id === state.selectedPluginId);
  }

  // eslint-disable-next-line
  public static getPlugin(pluginId: string) {
    return createSelector([PluginsState], (state: PluginsStateModel) => state.plugins.find(plugin => plugin.id === pluginId));
  }

  @Action(FetchAllPluginsBasicInfo)
  public fetchAll({ getState, setState }: StateContext<PluginsStateModel>): Observable<PluginWithSettings[]> {
    setState(patch<PluginsStateModel>({
      arePluginsLoading: true
    })
    );

    return this.pluginsService.getAllBasicInfo()
      .pipe(
        tap((plugins: PluginWithSettings[]) => {
          const state = getState();
          setState({
            ...state,
            plugins,
            arePluginsLoading: false,
            arePluginsLoaded: true
          });
        })
      );
  }

  @Action(FetchPluginsForCustomer)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public fetchPluginsForCustomer({ getState, setState }: StateContext<PluginsStateModel>, { id }: FetchPluginsForCustomer): Observable<PluginWithSettings[]> {
    return this.pluginsService.getAllPluginsForCustomer(id)
      .pipe(
        tap((customerPlugins: PluginWithSettings[]) => {
          const state = getState();
          setState({
            ...state,
            customerPlugins,
          });
        })
      );
  }

  @Action(FetchPlugin)
  public fetchSingle({ setState }: StateContext<PluginsStateModel>, { id }: FetchPlugin): Observable<PluginWithSettings> {
    return this.pluginsService.getPlugin(id)
      .pipe(
        tap((plugin: PluginWithSettings) => {
          setState(
            patch<PluginsStateModel>({
              plugins: iif<PluginWithSettings[]>(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                plugins => plugins!.some(x => x.id === plugin.id),
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                updateItem<PluginWithSettings>((x: PluginWithSettings) => x!.id === plugin.id, plugin),
                insertItem<PluginWithSettings>(plugin)
              )
            })
          );
        })
      );
  }

  @Action(SetCurrentPluginId)
  public setCurrentPluginId({ setState }: StateContext<PluginsStateModel>, { id }: SetCurrentPluginId): void {
    setState(
      patch({
        selectedPluginId: id
      })
    );
  }

  @Action(ClearCurrentPluginId)
  public clearCurrentPLuginId({ setState }: StateContext<PluginsStateModel>): void {
    setState(
      patch({
        selectedPluginId: ''
      })
    );
  }

  @Action(StartPlugin)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public startPlugin({ getState, setState }: StateContext<PluginsStateModel>, { id }: StartPlugin): Observable<any> {
    return this.pluginsService.startPlugin(id)
      .pipe(
        tap(() => {
          const state = getState();
          const plugin = state.plugins.find(p => p.id === id);

          if (plugin !== undefined) {
            const updatedPlugin = { ...plugin, pluginState: PluginState.idle };

            setState(
              patch({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                plugins: updateItem<PluginWithSettings>((x: PluginWithSettings) => x!.id === id, updatedPlugin)
              })
            );
          }
        })
      );
  }

  @Action(StopPlugin)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public stopPlugin({ getState, setState }: StateContext<PluginsStateModel>, { id }: StopPlugin): Observable<any> {
    return this.pluginsService.stopPlugin(id)
      .pipe(
        tap(() => {
          const state = getState();
          const plugin = state.plugins.find(p => p.id === id);

          if (plugin !== undefined) {
            const updatedPlugin = { ...plugin, pluginState: PluginState.paused };

            setState(
              patch({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                plugins: updateItem<PluginWithSettings>((x: PluginWithSettings) => x.id === id, updatedPlugin)
              })
            );
          }
        })
      );
  }

  @Action(EnablePluginTestMode)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public enablePluginTestMode({ getState, setState }: StateContext<PluginsStateModel>, { id }: EnablePluginTestMode): Observable<any> {
    return this.pluginsService.enablePluginTestMode(id)
      .pipe(
        tap(() => {
          const state = getState();
          const plugin = state.plugins.find(p => p.id === id);

          if (plugin !== undefined) {
            const updatedPlugin = { ...plugin, pluginState: PluginState.testmode };

            setState(
              patch({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                plugins: updateItem<PluginWithSettings>((x: PluginWithSettings) => x!.id === id, updatedPlugin)
              })
            );
          }
        })
      );
  }

  @Action(DeletePlugin)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public delete({ setState }: StateContext<PluginsStateModel>, { id }: DeletePlugin): Observable<any> {
    return this.pluginsService.deletePlugin(id)
      .pipe(
        tap(() => {
          setState(
            patch({
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              plugins: removeItem<PluginWithSettings>((x: PluginWithSettings) => x.id === id),
              selectedPluginId: ''
            })
          );
        })
      );
  }

  @Action(UpdatePlugin)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public update({ dispatch }: StateContext<PluginsStateModel>, { payload }: UpdatePlugin): Observable<any> {
    return this.pluginsService.updatePlugin(payload as PluginForm)
      .pipe(
        tap(() => {
          dispatch(new FetchPlugin(payload.id));
        })
      );
  }

  @Action(UpdatePluginConfigurationFile)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public updatePluginConfigurationFile({ dispatch }: StateContext<PluginsStateModel>, { pluginId, payload }: UpdatePluginConfigurationFile): Observable<any> {
    return this.pluginsService.updatePluginConfigurationFile(pluginId, payload)
      .pipe(
        tap(() => {
          dispatch(new FetchPlugin(pluginId));
        })
      );
  }

  @Action(AddPluginLaboratorySpecificSetting)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public addPluginLaboratorySpecificSetting({ dispatch }: StateContext<PluginsStateModel>, { payload }: AddPluginLaboratorySpecificSetting): Observable<any> {
    return this.pluginsService.addPluginLaboratorySpecificSetting(payload)
      .pipe(
        tap(() => {
          dispatch(new FetchPlugin(payload.contextModelId));
        })
      );
  }

  @Action(UpdatePluginLaboratorySpecificSetting)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public updatePluginLaboratorySpecificSetting({ dispatch }: StateContext<PluginsStateModel>, { payload }: UpdatePluginLaboratorySpecificSetting): Observable<any> {
    return this.pluginsService.updatePluginLaboratorySpecificSetting(payload)
      .pipe(
        tap(() => {
          dispatch(new FetchPlugin(payload.contextModelId));
        })
      );
  }

  @Action(DeletePluginLaboratorySpecificSetting)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public deletePluginLaboratorySpecificSetting({ dispatch }: StateContext<PluginsStateModel>, { pluginId, settingId }: DeletePluginLaboratorySpecificSetting): Observable<any> {
    return this.pluginsService.deletePluginLaboratorySpecificSetting(pluginId, settingId)
      .pipe(
        tap(() => {
          dispatch(new FetchPlugin(pluginId));
        })
      );
  }
}
